import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../AuthContext";
import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import db from "../Firebase";

function LogoSVG({ size = 34 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 34 34" fill="none">
      <rect width="34" height="34" rx="9" fill="rgba(13,189,176,0.10)" />
      <rect width="34" height="34" rx="9" stroke="rgba(13,189,176,0.30)" strokeWidth="0.5" />
      <circle cx="15" cy="13" r="5" stroke="#0DBDB0" strokeWidth="1.5" fill="none" />
      <path d="M7 27 C7 21 10 18 15 18 C20 18 23 21 23 27" stroke="#0DBDB0" strokeWidth="1.5" strokeLinecap="round" fill="none" />
      <path d="M23 11 L27 11 L27 23" stroke="#F5A623" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <circle cx="27" cy="23" r="1.8" fill="#F5A623" />
    </svg>
  );
}

const FONT_LINK = `@import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500;600;700;800;900&family=DM+Serif+Display:ital@0;1&display=swap');`;

const CSS = `
${FONT_LINK}

*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

:root {
  --ink:            #F0FAFA;
  --teal:           #0AADA1;
  --teal2:          #0EA89C;
  --teal3:          #0C9189;
  --teal-dim:       rgba(10,173,161,0.14);
  --teal-subtle:    rgba(10,173,161,0.08);
  --teal-glow:      rgba(10,173,161,0.22);
  --amber:          #E8920A;
  --text-primary:   #0D2B28;
  --text-muted:     rgba(15,50,45,0.62);
  --text-muted2:    rgba(15,50,45,0.38);
  --border:         rgba(10,173,161,0.14);
  --border2:        rgba(10,173,161,0.10);
  --border-teal:    rgba(10,173,161,0.22);
  --surface:        #FFFFFF;
  --surface2:       #F5FDFC;
  --surface3:       #EBF9F8;
  --serif:  'DM Serif Display', serif;
  --sans:   'Outfit', sans-serif;
}

html { scroll-behavior: smooth; overflow-x: hidden; }
body { background: var(--ink); color: var(--text-primary); font-family: var(--sans); overflow-x: hidden; }
::-webkit-scrollbar { width: 4px; }
::-webkit-scrollbar-track { background: #E8F7F6; }
::-webkit-scrollbar-thumb { background: rgba(10,173,161,0.35); border-radius: 2px; }

/* NAV */
.cp-nav { position:fixed; top:0; left:0; right:0; z-index:200; padding:1.4rem 3rem; transition:all 0.4s; }
.cp-nav.scrolled { background:rgba(240,250,250,0.96); backdrop-filter:blur(20px); border-bottom:0.5px solid var(--border); padding:0.9rem 3rem; box-shadow:0 4px 32px rgba(10,173,161,0.06); }
.cp-nav-inner { max-width:1180px; margin:0 auto; display:flex; align-items:center; justify-content:space-between; }
.cp-logo { display:flex; align-items:center; gap:10px; font-family:var(--sans); font-size:15px; font-weight:700; color:var(--text-primary); text-decoration:none; }
.cp-logo em { color:var(--teal); font-style:normal; }
.cp-nav-links { display:flex; align-items:center; gap:2.5rem; }
.cp-nav-link { font-size:13.5px; font-weight:500; color:var(--text-muted); text-decoration:none; transition:color 0.2s; }
.cp-nav-link:hover { color:var(--teal); }
.cp-nav-cta { padding:10px 22px; background:linear-gradient(135deg,var(--teal3),var(--teal)); color:#fff; border:none; border-radius:10px; font-family:var(--sans); font-size:13px; font-weight:700; cursor:pointer; transition:transform 0.15s,box-shadow 0.15s; }
.cp-nav-cta:hover { transform:translateY(-1px); box-shadow:0 6px 20px var(--teal-glow); }

/* HAMBURGER */
.cp-hamburger { display:none; flex-direction:column; justify-content:center; align-items:center; gap:5px; width:40px; height:40px; border-radius:10px; border:0.5px solid var(--border-teal); background:var(--teal-subtle); cursor:pointer; }
.cp-hamburger span { display:block; width:18px; height:1.5px; background:var(--teal); border-radius:2px; transition:all 0.3s; transform-origin:center; }
.cp-hamburger.open span:nth-child(1){transform:translateY(6.5px) rotate(45deg);}
.cp-hamburger.open span:nth-child(2){opacity:0;transform:scaleX(0);}
.cp-hamburger.open span:nth-child(3){transform:translateY(-6.5px) rotate(-45deg);}

/* MOBILE MENU */
.cp-mobile-menu { position:fixed; top:0; left:0; right:0; bottom:0; z-index:210; display:flex; flex-direction:column; pointer-events:none; }
.cp-mobile-backdrop { position:absolute; inset:0; background:rgba(5,10,20,0.6); backdrop-filter:blur(6px); opacity:0; transition:opacity 0.35s; }
.cp-mobile-menu.open .cp-mobile-backdrop { opacity:1; pointer-events:all; }
.cp-mobile-panel { position:absolute; top:0; right:0; width:min(320px,85vw); height:100%; background:#fff; border-left:0.5px solid var(--border-teal); display:flex; flex-direction:column; transform:translateX(100%); transition:transform 0.4s cubic-bezier(0.22,1,0.36,1); pointer-events:none; overflow:hidden; }
.cp-mobile-menu.open .cp-mobile-panel { transform:translateX(0); pointer-events:all; }
.cp-mobile-header { display:flex; align-items:center; justify-content:space-between; padding:1.25rem 1.5rem; border-bottom:0.5px solid var(--border2); }
.cp-mobile-close { width:36px; height:36px; border-radius:9px; border:0.5px solid var(--border2); background:var(--surface2); display:flex; align-items:center; justify-content:center; cursor:pointer; font-size:16px; color:var(--text-muted); }
.cp-mobile-links { flex:1; display:flex; flex-direction:column; padding:1.5rem; gap:0.25rem; }
.cp-mobile-link { display:flex; align-items:center; gap:12px; padding:1rem 1.25rem; border-radius:14px; font-size:15px; font-weight:600; color:var(--text-muted); text-decoration:none; opacity:0; transform:translateX(20px); transition:background 0.2s,color 0.2s,opacity 0.35s,transform 0.35s; }
.cp-mobile-menu.open .cp-mobile-link { opacity:1; transform:translateX(0); }
.cp-mobile-link:nth-child(1){transition-delay:0.06s;} .cp-mobile-link:nth-child(2){transition-delay:0.11s;} .cp-mobile-link:nth-child(3){transition-delay:0.16s;}
.cp-mobile-link:hover { background:var(--surface3); color:var(--teal); }
.cp-mobile-footer { padding:1.5rem; border-top:0.5px solid var(--border2); opacity:0; transform:translateY(12px); transition:opacity 0.35s ease 0.22s,transform 0.35s ease 0.22s; }
.cp-mobile-menu.open .cp-mobile-footer { opacity:1; transform:translateY(0); }
.cp-mobile-cta-btn { width:100%; padding:14px; background:linear-gradient(135deg,var(--teal3),var(--teal)); color:#fff; border:none; border-radius:12px; font-family:var(--sans); font-size:14px; font-weight:700; cursor:pointer; }

/* PAGE */
.cp-page { min-height:100vh; background:var(--ink); padding-top:6rem; }

/* HERO */
.cp-hero { max-width:1180px; margin:0 auto; padding:4rem 3rem 3rem; text-align:center; position:relative; }
.cp-hero::before { content:''; position:absolute; width:700px; height:700px; border-radius:50%; background:radial-gradient(circle,rgba(10,173,161,0.08) 0%,transparent 70%); top:-200px; left:50%; transform:translateX(-50%); pointer-events:none; }
.cp-eyebrow { display:inline-flex; align-items:center; gap:8px; padding:7px 16px; background:rgba(13,189,176,0.08); border:0.5px solid rgba(13,189,176,0.3); border-radius:100px; font-size:11.5px; font-weight:700; color:var(--teal); letter-spacing:1px; text-transform:uppercase; margin-bottom:1.5rem; }
.cp-eyebrow-dot { width:6px; height:6px; border-radius:50%; background:var(--teal); animation:pulse-teal 2s ease-in-out infinite; }
@keyframes pulse-teal { 0%,100%{opacity:1;transform:scale(1);} 50%{opacity:0.3;transform:scale(0.65);} }
.cp-title { font-family:var(--sans); font-size:clamp(2.2rem,4.5vw,3.8rem); font-weight:800; line-height:1.1; letter-spacing:-1px; color:var(--text-primary); margin-bottom:1rem; }
.cp-title span { background:linear-gradient(100deg,#0DBDB0 0%,#0DBDB0 40%,#F5A623 100%); -webkit-background-clip:text; -webkit-text-fill-color:transparent; background-clip:text; }
.cp-subtitle { font-size:16px; color:var(--text-muted); line-height:1.8; max-width:520px; margin:0 auto; }

/* MAIN CONTENT */
.cp-content { max-width:1100px; margin:0 auto; padding:3rem 3rem 6rem; display:grid; grid-template-columns:1fr 1.6fr; gap:3rem; align-items:start; }

/* INFO CARDS */
.cp-info-col { display:flex; flex-direction:column; gap:1.25rem; }
.cp-info-card { background:var(--surface); border:0.5px solid var(--border-teal); border-radius:20px; padding:1.75rem; display:flex; align-items:flex-start; gap:1rem; transition:box-shadow 0.25s,transform 0.25s; }
.cp-info-card:hover { box-shadow:0 8px 32px rgba(10,173,161,0.10); transform:translateY(-2px); }
.cp-info-icon { width:48px; height:48px; border-radius:14px; display:flex; align-items:center; justify-content:center; font-size:20px; flex-shrink:0; }
.cp-info-icon.teal { background:rgba(10,173,161,0.10); }
.cp-info-icon.amber { background:rgba(232,146,10,0.10); }
.cp-info-icon.coral { background:rgba(255,107,107,0.10); }
.cp-info-body h4 { font-size:13px; font-weight:700; color:var(--text-muted2); text-transform:uppercase; letter-spacing:0.8px; margin-bottom:0.4rem; }
.cp-info-body a { font-size:15px; font-weight:600; color:var(--text-primary); text-decoration:none; transition:color 0.2s; display:block; }
.cp-info-body a:hover { color:var(--teal); }
.cp-info-body p { font-size:13px; color:var(--text-muted); margin-top:0.25rem; line-height:1.5; }

.cp-social-row { display:flex; gap:0.75rem; margin-top:0.5rem; }
.cp-social-btn { width:38px; height:38px; border-radius:10px; background:var(--surface2); border:0.5px solid var(--border2); display:flex; align-items:center; justify-content:center; font-size:16px; text-decoration:none; transition:background 0.2s,border-color 0.2s,transform 0.15s; }
.cp-social-btn:hover { background:var(--surface3); border-color:var(--border-teal); transform:translateY(-2px); }

.cp-response-badge { margin-top:1.25rem; padding:1rem 1.25rem; background:linear-gradient(135deg,rgba(10,173,161,0.06),rgba(10,173,161,0.03)); border:0.5px solid var(--border-teal); border-radius:14px; display:flex; align-items:center; gap:10px; font-size:13px; color:var(--text-muted); }
.cp-response-badge-dot { width:8px; height:8px; border-radius:50%; background:#22c55e; flex-shrink:0; box-shadow:0 0 0 3px rgba(34,197,94,0.2); animation:pulse-green 2s infinite; }
@keyframes pulse-green { 0%,100%{box-shadow:0 0 0 3px rgba(34,197,94,0.2);} 50%{box-shadow:0 0 0 6px rgba(34,197,94,0.05);} }

/* FORM */
.cp-form-card { background:var(--surface); border:0.5px solid var(--border-teal); border-radius:24px; padding:2.5rem; box-shadow:0 4px 40px rgba(10,173,161,0.06); }
.cp-form-title { font-size:20px; font-weight:700; color:var(--text-primary); margin-bottom:0.5rem; }
.cp-form-sub { font-size:13.5px; color:var(--text-muted); margin-bottom:2rem; }
.cp-form { display:flex; flex-direction:column; gap:1.25rem; }
.cp-row { display:grid; grid-template-columns:1fr 1fr; gap:1.25rem; }
.cp-field { display:flex; flex-direction:column; gap:0.5rem; }
.cp-label { font-size:12.5px; font-weight:600; color:var(--text-muted); text-transform:uppercase; letter-spacing:0.6px; }
.cp-input, .cp-textarea, .cp-select { font-family:var(--sans); font-size:14px; font-weight:500; color:var(--text-primary); background:var(--surface2); border:1px solid var(--border2); border-radius:12px; padding:0.85rem 1rem; outline:none; transition:border-color 0.2s,box-shadow 0.2s,background 0.2s; width:100%; }
.cp-input:focus, .cp-textarea:focus, .cp-select:focus { border-color:var(--teal); box-shadow:0 0 0 3px rgba(10,173,161,0.12); background:#fff; }
.cp-input::placeholder, .cp-textarea::placeholder { color:var(--text-muted2); }
.cp-textarea { resize:vertical; min-height:130px; line-height:1.6; }
.cp-select { appearance:none; cursor:pointer; }

.cp-submit { display:flex; align-items:center; justify-content:center; gap:10px; padding:1rem 2rem; background:linear-gradient(135deg,var(--teal3),var(--teal)); color:#fff; border:none; border-radius:14px; font-family:var(--sans); font-size:15px; font-weight:700; cursor:pointer; position:relative; overflow:hidden; transition:transform 0.15s,box-shadow 0.2s; width:100%; margin-top:0.5rem; }
.cp-submit::before { content:''; position:absolute; inset:0; background:linear-gradient(135deg,rgba(255,255,255,0.15),transparent 60%); pointer-events:none; }
.cp-submit:hover:not(:disabled) { transform:translateY(-2px); box-shadow:0 8px 28px var(--teal-glow); }
.cp-submit:disabled { opacity:0.7; cursor:not-allowed; }
.cp-submit-icon { font-size:16px; transition:transform 0.2s; }
.cp-submit:hover:not(:disabled) .cp-submit-icon { transform:translateX(4px); }

/* SUCCESS */
.cp-success { text-align:center; padding:3rem 2rem; }
.cp-success-icon { font-size:56px; margin-bottom:1rem; }
.cp-success-title { font-size:22px; font-weight:700; color:var(--text-primary); margin-bottom:0.75rem; }
.cp-success-sub { font-size:14px; color:var(--text-muted); line-height:1.7; margin-bottom:2rem; }
.cp-success-back { padding:0.85rem 2rem; background:linear-gradient(135deg,var(--teal3),var(--teal)); color:#fff; border:none; border-radius:12px; font-family:var(--sans); font-size:14px; font-weight:700; cursor:pointer; }

/* FOOTER */
.cp-footer { background:var(--surface); border-top:0.5px solid var(--border2); padding:2rem 3rem; text-align:center; }
.cp-footer-inner { max-width:1180px; margin:0 auto; display:flex; align-items:center; justify-content:space-between; flex-wrap:wrap; gap:1rem; }
.cp-footer-logo { font-size:14px; font-weight:700; color:var(--text-primary); }
.cp-footer-logo em { color:var(--teal); font-style:normal; }
.cp-footer-copy { font-size:12px; color:var(--text-muted2); }
.cp-footer-links { display:flex; gap:1.5rem; }
.cp-footer-link { font-size:12.5px; color:var(--text-muted); text-decoration:none; transition:color 0.2s; }
.cp-footer-link:hover { color:var(--teal); }

/* RESPONSIVE */
@media(max-width:900px){
  .cp-content { grid-template-columns:1fr; gap:2rem; }
  .cp-nav-links { display:none; }
  .cp-hamburger { display:flex; }
}
@media(max-width:600px){
  .cp-nav { padding:1rem 1.25rem; }
  .cp-nav.scrolled { padding:0.75rem 1.25rem; }
  .cp-hero { padding:3rem 1.25rem 2rem; }
  .cp-content { padding:2rem 1.25rem 4rem; }
  .cp-row { grid-template-columns:1fr; }
  .cp-form-card { padding:1.75rem 1.25rem; }
  .cp-footer { padding:1.5rem 1.25rem; }
  .cp-footer-inner { flex-direction:column; text-align:center; }
}
`;

export default function ContactPage() {
  const navigate = useNavigate();
  const { user, signIn, signOut } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    subject: "General Inquiry",
    message: "",
  });

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const handleChange = (e) => {
    setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await addDoc(collection(db, "contactMessages"), {
        name: form.name,
        email: form.email,
        subject: form.subject,
        message: form.message,
        createdAt: serverTimestamp(),
        read: false,
      });
      setSubmitted(true);
    } catch (err) {
      console.error("Failed to send message:", err);
      alert("Something went wrong. Please try again or email us directly at myprofilecorner@gmail.com");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <style>{CSS}</style>

      {/* NAV */}
      <nav className={`cp-nav${scrolled ? " scrolled" : ""}`}>
        <div className="cp-nav-inner">
          <a href="/" className="cp-logo">
            <LogoSVG size={34} />myprofile<em>corner</em>
          </a>
          <div className="cp-nav-links">
            <a href="/" className="cp-nav-link">Home</a>
            <a href="/#features" className="cp-nav-link">Features</a>
            <a href="/contact" className="cp-nav-link" style={{ color: "var(--teal)", fontWeight: 700 }}>Contact</a>
            {user ? (
              <button className="cp-nav-cta" onClick={() => navigate("/create")}>My Card</button>
            ) : (
              <button className="cp-nav-cta" onClick={signIn}>Get Started</button>
            )}
          </div>
          <button
            className={`cp-hamburger${menuOpen ? " open" : ""}`}
            onClick={() => setMenuOpen((p) => !p)}
            aria-label="Toggle menu"
          >
            <span /><span /><span />
          </button>
        </div>
      </nav>

      {/* MOBILE MENU */}
      <div className={`cp-mobile-menu${menuOpen ? " open" : ""}`}>
        <div className="cp-mobile-backdrop" onClick={() => setMenuOpen(false)} />
        <div className="cp-mobile-panel">
          <div className="cp-mobile-header">
            <a href="/" className="cp-logo" onClick={() => setMenuOpen(false)}><LogoSVG size={28} />myprofile<em>corner</em></a>
            <button className="cp-mobile-close" onClick={() => setMenuOpen(false)}>✕</button>
          </div>
          <div className="cp-mobile-links">
            <a href="/" className="cp-mobile-link" onClick={() => setMenuOpen(false)}>🏠 Home</a>
            <a href="/#features" className="cp-mobile-link" onClick={() => setMenuOpen(false)}>✨ Features</a>
            <a href="/contact" className="cp-mobile-link" onClick={() => setMenuOpen(false)}>✉️ Contact</a>
          </div>
          <div className="cp-mobile-footer">
            {user ? (
              <button className="cp-mobile-cta-btn" onClick={() => { navigate("/create"); setMenuOpen(false); }}>My Card</button>
            ) : (
              <button className="cp-mobile-cta-btn" onClick={() => { signIn(); setMenuOpen(false); }}>Get Started Free</button>
            )}
          </div>
        </div>
      </div>

      {/* PAGE */}
      <div className="cp-page">
        {/* HERO */}
        <div className="cp-hero">
          <div className="cp-eyebrow">
            <div className="cp-eyebrow-dot" />
            Get in Touch
          </div>
          <h1 className="cp-title">
            We'd love to <span>hear from you</span>
          </h1>
          <p className="cp-subtitle">
            Have a question, feedback, or just want to say hello? Drop us a message and we'll get back to you as soon as possible.
          </p>
        </div>

        {/* CONTENT */}
        <div className="cp-content">
          {/* INFO */}
          <div className="cp-info-col">
            <div className="cp-info-card">
              <div className="cp-info-icon teal">📧</div>
              <div className="cp-info-body">
                <h4>Email Us</h4>
                <a href="mailto:myprofilecorner@gmail.com">myprofilecorner@gmail.com</a>
                <p>For support, inquiries & partnerships</p>
              </div>
            </div>

            <div className="cp-info-card">
              <div className="cp-info-icon amber">📞</div>
              <div className="cp-info-body">
                <h4>Call Us</h4>
                <a href="tel:+917856946583">+91 7856946583</a>
                <p>Mon – Sat, 10 AM – 7 PM IST</p>
              </div>
            </div>

            {/* <div className="cp-info-card">
              <div className="cp-info-icon coral">🌐</div>
              <div className="cp-info-body">
                <h4>Follow Us</h4>
                <p style={{ marginBottom: "0.75rem" }}>Stay updated on social media</p>
                <div className="cp-social-row">
                  <a href="https://instagram.com/myprofilecorner" target="_blank" rel="noopener noreferrer" className="cp-social-btn" title="Instagram">📸</a>
                  <a href="https://twitter.com/myprofilecorner" target="_blank" rel="noopener noreferrer" className="cp-social-btn" title="Twitter">🐦</a>
                  <a href="https://linkedin.com/company/myprofilecorner" target="_blank" rel="noopener noreferrer" className="cp-social-btn" title="LinkedIn">💼</a>
                </div>
              </div>
            </div> */}

            <div className="cp-response-badge">
              <div className="cp-response-badge-dot" />
              <span>We typically respond within <strong>&nbsp;24 hours</strong></span>
            </div>
          </div>

          {/* FORM */}
          <div className="cp-form-card">
            {submitted ? (
              <div className="cp-success">
                <div className="cp-success-icon">✅</div>
                <h3 className="cp-success-title">Message Sent!</h3>
                <p className="cp-success-sub">
                  Thanks for reaching out! We've received your message and will get back to you within 24 hours at <strong>{form.email}</strong>.
                </p>
                <button className="cp-success-back" onClick={() => setSubmitted(false)}>Send Another Message</button>
              </div>
            ) : (
              <>
                <h2 className="cp-form-title">Send us a message</h2>
                <p className="cp-form-sub">Fill out the form below and we'll be in touch shortly.</p>
                <form className="cp-form" onSubmit={handleSubmit}>
                  <div className="cp-row">
                    <div className="cp-field">
                      <label className="cp-label">Your Name *</label>
                      <input
                        className="cp-input"
                        name="name"
                        value={form.name}
                        onChange={handleChange}
                        placeholder="Enter your name"
                        required
                      />
                    </div>
                    <div className="cp-field">
                      <label className="cp-label">Email Address *</label>
                      <input
                        className="cp-input"
                        name="email"
                        type="email"
                        value={form.email}
                        onChange={handleChange}
                        placeholder="you@example.com"
                        required
                      />
                    </div>
                  </div>

                  <div className="cp-field">
                    <label className="cp-label">Subject</label>
                    <select
                      className="cp-select"
                      name="subject"
                      value={form.subject}
                      onChange={handleChange}
                    >
                      <option>General Inquiry</option>
                      <option>Technical Support</option>
                      <option>Feature Request</option>
                      <option>Partnership / Collaboration</option>
                      <option>Bug Report</option>
                      <option>Other</option>
                    </select>
                  </div>

                  <div className="cp-field">
                    <label className="cp-label">Message *</label>
                    <textarea
                      className="cp-textarea"
                      name="message"
                      value={form.message}
                      onChange={handleChange}
                      placeholder="Tell us how we can help you..."
                      required
                    />
                  </div>

                  <button className="cp-submit" type="submit" disabled={loading}>
                    {loading ? "Sending…" : "Send Message"}
                    <span className="cp-submit-icon">→</span>
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      {/* FOOTER */}
      <footer className="cp-footer">
        <div className="cp-footer-inner">
          <div className="cp-footer-logo" style={{ display: "flex", alignItems: "center", gap: "8px" }}><LogoSVG size={26} />myprofile<em>corner</em></div>
          <div className="cp-footer-copy">© {new Date().getFullYear()} MyProfileCorner. All rights reserved.</div>
          <div className="cp-footer-links">
            <a href="/" className="cp-footer-link">Home</a>
            <a href="/contact" className="cp-footer-link">Contact</a>
          </div>
        </div>
      </footer>
    </>
  );
}
